<script setup>
import { ref, onMounted } from 'vue';

// // Store management imports
import { useBreakStore } from '@/stores/break';
import { sendInteractionCompleted } from '@/utils/nacxMetrics';

// // Composables
import { useAssetLoading } from '@/composables/useAssetLoading';
import { useNacxMetrics } from '@/composables/useNacxMetrics';
import { useOverlayNavigation } from '@/composables/useOverlayNavigation';
import { useOverlayTimeline } from '@/composables/useOverlayTimeline';

// // Module imports
import TriviaInput from './TriviaInput.vue';
import TriviaOutput from './TriviaOutput.vue';
import TriviaTidbit from './TriviaTidbit.vue';

// // Asset loading
const { isAssetsLoaded, isTemplateReadyToFadeIn, waitForComponentAssets, templateFadeInHandler } =
  useAssetLoading(5000);
const containerRef = ref();

// // Timeline
const spinnerDurationMS = 1500;
const introDurationMS = 3000;
const dismissDurationMS = 500;

const {
  currentScreen: triviaState,
  isDismissing,
  isGone,
  dismiss,
  skipToScreen,
  getScreenDuration,
} = useOverlayTimeline({
  screens: [
    { name: 'spinner', duration: spinnerDurationMS },
    { name: 'intro', duration: introDurationMS },
    { name: 'question', minDuration: 13000, maxDuration: 15000 },
    { name: 'result', minDuration: 5000, maxDuration: 7000 },
    { name: 'tidbit', minDuration: 13000, maxDuration: 15000 },
    { name: 'dismiss', duration: dismissDurationMS },
  ],
  maxOnScreenDuration: 42000,
  fallbackSlotDuration: 60000,
});

const storeBreak = useBreakStore();

// markDismissed is for user-initiated dismiss only — there's no auto-fired
// "closed" event; when the overlay actually closes is up to the TV parent.
// Arrow/Enter are fully consumed by useOverlayNavigation below, so we don't
// forward them to TV Plus (still tracked as NACX key_press metrics though).
const { markDismissed, trackKeyPress } = useNacxMetrics({
  suppressForwardingKeys: ['ArrowUp', 'ArrowDown', 'Enter'],
});

const hasDismissalButton = ref(true);
const hasProgressBar = ref(true);

// // TempTriviaPoll handler functions

const templateFadeOutHandler = () => {
  dismiss();
};

const { activeIndex, selectedIndex, isDismissInFocus } = useOverlayNavigation({
  trackKeyPress,
  currentScreen: triviaState,
  getOptionCount: () => storeBreak.breakOptions.length,
  postQuestionScreens: ['result', 'tidbit'],
  hasDismissalButton,
  onSelect: () => handleOptionSelected(),
  onDismiss: () => {
    markDismissed();
    templateFadeOutHandler();
  },
});

const handleOptionSelected = async () => {
  // hasNavigationFrozen is already set by useOverlayNavigation before onSelect fires.
  selectedIndex.value = activeIndex.value;

  // // NACX Metrics
  const selectedLabel = storeBreak.breakOptions[activeIndex.value]?.optionLabel;
  const isCorrect = selectedLabel === storeBreak.breakCorrectOption;
  sendInteractionCompleted({
    interaction: {
      type: 'question_answered',
      entity: [
        { type: 'option_selected', value: String(activeIndex.value + 1) },
        { type: 'is_correct', value: isCorrect ? 'yes' : 'no' },
      ],
    },
  });

  skipToScreen('result');
};

// // TempTriviaPoll lifecycle hooks

onMounted(async () => {
  // // Wait for all assets to load before showing content
  await waitForComponentAssets(containerRef.value);
  templateFadeInHandler();
});
</script>

<template>
  <!-- v-show="isAssetsLoaded"
  ref="containerRef" -->
  <section
    class="trivia-poll"
    :class="{
      'is-clear': !isTemplateReadyToFadeIn || !isAssetsLoaded,
      'is-opaque': isTemplateReadyToFadeIn && isAssetsLoaded,
      'trivia-poll--vanish': isDismissing,
      'is-gone': isGone,
    }"
  >
    <div class="trivia-poll__container">
      <div v-if="triviaState === 'spinner'" class="trivia-poll__spinner"></div>

      <div
        class="trivia-poll__intro"
        :class="{
          'trivia-poll__intro--closed': triviaState !== 'intro',
          'trivia-poll__intro--open': triviaState === 'intro',
        }"
      >
        <span class="trivia-poll__intro-copy">Grab your remote</span>
      </div>

      <!-- v-show="triviaState === 'question'" -->
      <TriviaInput
        :class="{
          'is-clear': triviaState !== 'question',
          'is-opaque': triviaState === 'question',
        }"
        :has-dismissal="hasDismissalButton"
        :has-progress="hasProgressBar"
        :has-progress-begun="triviaState === 'question'"
        :has-orbs="true"
        :is-focus-on-dismiss="isDismissInFocus"
        :question="storeBreak.breakTaglineCopy"
        :trivia-options="storeBreak.breakOptions"
        :focus-index="activeIndex"
        :progress-duration="getScreenDuration('question')"
        @component-ready="templateFadeInHandler"
      />

      <!-- v-show="triviaState === 'result'" -->
      <TriviaOutput
        :class="{
          'is-clear': triviaState !== 'result',
          'is-opaque': triviaState === 'result',
        }"
        :has-dismissal="hasDismissalButton"
        :has-progress="hasProgressBar"
        :has-progress-begun="triviaState === 'result'"
        :has-orbs="true"
        :is-focus-on-dismiss="isDismissInFocus"
        :question="storeBreak.breakTaglineCopy"
        :correct-option-label="storeBreak.breakCorrectOption"
        :trivia-options="storeBreak.breakOptions"
        :focus-index="activeIndex"
        :selected-index="selectedIndex"
        :progress-duration="getScreenDuration('result')"
      />

      <!-- <TriviaTidbit v-show="triviaState === 'tidbit'" :tidbit="storeBreak.breakTriviaTidbit" /> -->
      <TriviaTidbit
        :class="{
          'is-clear': triviaState !== 'tidbit' && triviaState !== 'dismiss',
          'is-opaque': triviaState === 'tidbit' || triviaState === 'dismiss',
        }"
        :has-dismissal="hasDismissalButton"
        :has-progress="hasProgressBar"
        :has-progress-begun="triviaState === 'tidbit'"
        :has-orbs="true"
        :is-focus-on-dismiss="isDismissInFocus"
        :tidbit-copy="storeBreak.breakTriviaTidbit"
        :focus-index="activeIndex"
        :progress-duration="getScreenDuration('tidbit')"
      />
    </div>
  </section>
</template>
